import { renderer } from '../Renderer';
import { ViewFactory } from '../Template';
import AttributeExpression from './AttributeExpression';
import DataAttributeExpression from './DataAttributeExpression';
import Expression, { snakeToCamel, hasExpression } from './Expression';
import ViewDataExpression from './ViewDataExpression';
import { Observable, observable } from '../../signal/Signal';
import View from '../View';
import CSSExpression from './CSSExpression';

class ViewExpression extends Expression {

	target:Element;
	attributes:{[key:string]:string};
	expressions:Expression[];
	viewFactory:ViewFactory;

	constructor( target:Element, viewFactory:ViewFactory ) {
		super();
		this.target = target;
		this.viewFactory = viewFactory;
		this.expressions = [];
		this.parseAttributes();
		this.template = this.attributes['view'];
	}

	update( data:any, attribute?:string ) {

		// check if we have an expression update of the view attribute
		if( super.hasAttribute( attribute ) ) {
			this.renderView( data );
		} else {
			// update expressions only
			this.expressions.forEach( exp => {
				if( exp.hasAttribute( attribute ) ) exp.update( data, attribute );
			});
		}
	}

	renderView( data ) {

		this.reset();

		let node = this.target;
		let cssExpression:CSSExpression;

		let viewName = this.attributes['view'];

		// if viewname is expression evaluate and write back
		if( hasExpression( viewName ) ) {
			viewName = renderer.render( viewName, data );
		}
		node.removeAttribute( 'view' );
		node['_lnviewname'] = this.getViewName( viewName );

		let view = this.viewFactory( viewName );

		// set data if there is any
		if( this.attributes['data'] ) {
			let dataExp = new ViewDataExpression( this, this.attributes['data'] );
			view.data = dataExp.value( data );
			this.expressions.push( dataExp );
			node.removeAttribute( 'data' );
		}

		// handle view attributes
		Object.keys( this.attributes ).forEach( name => {

			// ignore special view attribute
			if( name == 'view' || name == 'data' ) return;

			let value = this.attributes[ name ];

			// handle events
			if( name.indexOf( 'on-' ) == 0 ) {
				// get signal name an subscribe
				let signalName = snakeToCamel( name.replace( 'on-', '' ) );

				// look on data for signal
				if( view.data[ signalName ] instanceof Observable ) {
					subscribeTo( view.data[ signalName ], value, data );
				// otherwise register on node signal
				} else {
					subscribeTo( observable( signalName, node ), value, data );
				}

				node.removeAttribute( name );
				return;
			}


			let propName = snakeToCamel( name );
			const attributeValue = node.getAttribute( name );

			// check if viewModel has property otherwise its a node attribute
			if( view.data instanceof Object && propName in view.data ) {

				let dataExp = new DataAttributeExpression( view.data, propName, value );
				dataExp.update( data, null );
				this.expressions.push( dataExp );

				// remove node attribute
				node.removeAttribute( name );

			} else if( name == 'class' ) {
				cssExpression =  new CSSExpression( node, attributeValue );
				cssExpression.update( data );
				if( hasExpression( attributeValue ) ) this.expressions.push( cssExpression );

			} else if( hasExpression( attributeValue ) ) {
				let expr = new AttributeExpression( node, name, attributeValue );
				expr.update( data );
				this.expressions.push( expr );
			}
		});

		view.prepareRender( node );
		view.render( node );

		node.setAttribute( 'view', node['_lnviewname'] );

		// if no outer expression, nothing to merge
		if( cssExpression ) {
			this.mergeCSS( cssExpression, view );
		}
	}

	parseAttributes() {
		this.attributes = {};
		for (let i = 0; i < this.target.attributes.length; i++) {
			const attr = this.target.attributes[i];
            this.attributes[ attr.name ] = attr.value;
		}
	}

	/**
	 * Checks if the view="..." expression and all its sub expression have the attribute to update
	 * @param name The name of the attribute to check if its in expressions
	 */
	hasAttribute( name?:string ) {
		if( super.hasAttribute( name ) ) return true;
		return this.expressions.some( expr => expr.hasAttribute( name ) );
	}


	/**
	 * Handles the merging of css from outer tenplate and inner view template on the same node
	 * @param outer the outer cssexpression
	 * @param view the inner view that maybe has a css experssion on root node
	 */
	mergeCSS( outer:CSSExpression, view:View ) {


		let innerClass = view.node.getAttribute( 'class' );

		// no change happend - nothing to merge
		if( innerClass == outer.value ) return;

		// search for inner cssexpression on root node.
		let inner = view.template.expressions.filter( exp => exp instanceof CSSExpression && exp.node == view.node )[0] as CSSExpression;
		if( !inner ) {
			// if no expression was found but change in class -> static expression
			inner = new CSSExpression( view.node, '' );
			inner.value = innerClass;
		};

		// update mechanism to merge both
		function merge() {
			view.node.setAttribute( 'class', outer.value + ' ' + inner.value );
		}

		// handle update of one and the other
		inner.writeValue = function( value ) {
			inner.value = value;
			merge();
		}
		outer.writeValue = function( value ) {
			outer.value = value;
			merge();
		}

		// inital merge of both values.
		merge();
	}

	/**
	 * resets everything for a new rendering.
	 */
	reset() {
		// reset expresions
		this.expressions = [];

		// reset attributes
		Object.keys( this.attributes ).forEach( attr => {
			this.target.setAttribute( attr, this.attributes[ attr ] );
		});
	}

	getViewName( key:any ) {
		if( typeof key == 'string' ) return key;
		if( typeof key == 'function' ) return key.toString().match(/function\s(.+)\(/)[1];
		if( typeof key == 'object' ) return key.constructor.toString().match(/function\s(.+)\(/)[1];
		return '';
	}

}

export default ViewExpression;


/**
 * Helper function to subscribe to signals
 * @param observable the observable to subscribe
 * @param content the string content of the on-... attribute for the oberserver callback
 * @param data the data/viewModel to look callback functions
 */
export function subscribeTo( observable:Observable<any>, content:string, data:{} ) {
	// check for valid observable
	if( !observable ) return;

	// event closure
	if( hasExpression( content ) ) {

		let callback = renderer.compile( content, 'event,data' );

		observable.subscribe( ( event ) => {
			callback.call( renderer.context, event, data );
		});

	// viewModel function
	} else if( data[ content ] ) {
		observable.subscribe( data[ content ].bind( data ) );
	}
}

